import { IonCol, IonLabel, IonRow } from '@ionic/react';
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import BuildItemSelect from '../../build-item-select/build-item-select';
import './serato.scss';
import { on, removeListener, run } from '../../../../helper/electron';
import { iTrack } from '../../../../types/ITrack';
import { AgGridReact } from 'ag-grid-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { GridReadyEvent } from 'ag-grid-community';
import { v4 as uuidv4 } from 'uuid';
import { translateKey } from '../../../../utils/translateKey'; // ✅ Import the translateKey function

interface ImportHistoryProps {
  setCrateDescription: (description: string) => void;
  setTrackList: (trackList: iTrack[]) => void;
  setSuggestedCrateName?: (name: string) => void;
}

type TrackType = {
  filepath: string;
  artist: string;
  title: string;
  bpm: number;
  key: string;
  updated_at?: number;
};

const ImportHistory: React.FC<ImportHistoryProps> = ({
  setCrateDescription,
  setTrackList,
  setSuggestedCrateName,
}) => {
  const theme = useSelector((state: RootState) => state.themeState.theme);
  const [selectedHistory, setSelectedHistory] = useState<any[]>([]);
  const [historySessions, setHistorySessions] = useState([]);
  const [tracks, setTracks] = useState<TrackType[]>([]);
  const [gridApi, setGridApi] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  const getSeratoHistoryTracks = useCallback(
    (filename: string): Promise<any[]> => {
      return new Promise((resolve) => {
        let isResolved = false;

        const handleData = (data: any) => {
          if (isResolved) return;
          isResolved = true;

          removeListener('serato/get-history-tracks', handleData); // ✅ Only this handler
          clearTimeout(timeout);

          if (!data || !Array.isArray(data)) {
            console.warn(`Invalid or no data received for ${filename}`);
            resolve([]);
            return;
          }

          resolve(data);
        };

        const timeout = setTimeout(() => {
          if (isResolved) return;
          isResolved = true;

          removeListener('serato/get-history-tracks', handleData); // ✅ Only this handler
          console.warn(`Timeout loading history tracks for ${filename}`);
          resolve([]);
        }, 10000);

        on('serato/get-history-tracks', handleData);
        run('serato/get-history-tracks', filename);
      });
    },
    [],
  );

  const hackyTransform = useCallback(
    async (inputData: any[]): Promise<iTrack[]> => {
      // Get all of the tracks in order
      const sortedData = [...inputData].sort(
        (a, b) => (a.updated_at || 0) - (b.updated_at || 0),
      );

      const result = sortedData
        .filter((data) => data !== undefined && data !== null)
        .map((data, index) => {
          const camelotKey = data.key ? translateKey(data.key, 'camelot') : '';
          return {
            ID: uuidv4(),
            Artist: data.artist || '',
            Title: data.title || '',
            Preview: data.filepath || '',
            duration: 0,
            BPM: data.bpm ? Math.round(data.bpm) : 0,
            energy: 0,
            danceability: 0,
            valence: 0,
            Key_Camelot: camelotKey,
          };
        });
      return result;
    },
    [],
  );

  const generateCrateDescription = useCallback((sessions: any[]): string => {
    if (!sessions || sessions.length === 0) return '';

    const sortedSessions = [...sessions].sort(
      (a, b) => new Date(a.name).getTime() - new Date(b.name).getTime(),
    );

    return sortedSessions.length === 1
      ? sortedSessions[0].name
      : `${sortedSessions[0].name}-${sortedSessions[sortedSessions.length - 1].name}`;
  }, []);

  const generateSuggestedCrateName = useCallback((sessions: any[]): string => {
    if (!sessions || sessions.length === 0) return '';

    const sortedSessions = [...sessions].sort(
      (a, b) => new Date(a.name).getTime() - new Date(b.name).getTime(),
    );

    // Format: "Serato History [date]" or "Serato History [start date] - [end date]"
    if (sortedSessions.length === 1) {
      return `Serato History ${sortedSessions[0].name}`;
    }
    return `Serato History ${sortedSessions[0].name} - ${sortedSessions[sortedSessions.length - 1].name}`;
  }, []);

  const [sessionTracksMap, setSessionTracksMap] = useState<
    Record<string, TrackType[]>
  >({});

  const handleSelectedHistoryChange = useCallback(
    async (selectedSessions: any[]) => {
      const keyFor = (s: any) => s._uid || `${s.filename}::${s.name}`;

      const selectedKeys = selectedSessions.map((s) => keyFor(s));
      const prevKeys = Object.keys(sessionTracksMap);

      const updatedMap = { ...sessionTracksMap };
      prevKeys.forEach((k) => {
        if (!selectedKeys.includes(k)) {
          delete updatedMap[k];
        }
      });

      const toAdd = selectedSessions.filter(
        (s) => !prevKeys.includes(keyFor(s)),
      );

      if (toAdd.length > 0) {
        setIsLoading(true);
        try {
          const results = await Promise.all(
            toAdd.map(async (s) => {
              const tracks = await getSeratoHistoryTracks(s.filename);
              return {
                key: keyFor(s),
                tracks: Array.isArray(tracks) ? tracks : [],
              };
            }),
          );

          results.forEach(({ key, tracks }) => {
            updatedMap[key] = tracks;
          });
        } catch (err) {
          console.error('[ImportHistory] Error fetching session tracks:', err);
        } finally {
          setIsLoading(false);
        }
      }

      setSessionTracksMap(updatedMap);
      setSelectedHistory(selectedSessions);
      setCrateDescription(generateCrateDescription(selectedSessions));
      setSuggestedCrateName?.(generateSuggestedCrateName(selectedSessions));
    },
    [
      sessionTracksMap,
      getSeratoHistoryTracks,
      generateCrateDescription,
      generateSuggestedCrateName,
      setSuggestedCrateName,
    ],
  );

  useEffect(() => {
    const mergedTracks = Object.values(sessionTracksMap)
      .flat()
      .map((track) => ({
        ...track,
        bpm: track.bpm ? Math.round(track.bpm) : 0,
      }));

    setTracks(mergedTracks);
    hackyTransform(mergedTracks).then(setTrackList);
  }, [sessionTracksMap, hackyTransform]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
  }, []);

  const columnDefs = useMemo(
    () => [
      { field: 'id', hide: true },
      {
        field: 'artist',
        headerName: 'Artist',
        flex: 1,
        resizable: true,
        sortable: true,
        suppressMenu: true,
      },
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        resizable: true,
        sortable: true,
        suppressMenu: true,
      },
      {
        field: 'bpm',
        headerName: 'BPM',
        width: 70,
        resizable: true,
        sortable: true,
        suppressMenu: true,
      },
      {
        field: 'key',
        headerName: 'Key',
        width: 70,
        resizable: true,
        sortable: true,
        suppressMenu: true,
      },
    ],
    [],
  );

  useEffect(() => {
    let mounted = true;

    const handleHistorySessions = (data: any) => {
      if (mounted) {
        const withIds = (data || []).map((item: any, idx: any) => ({
          ...item,
          _uid: `${item.filename}::${item.name}::${idx}`,
        }));
        setHistorySessions(withIds);
      }
    };

    on('serato/get-history-sessions', handleHistorySessions);
    run('serato/get-history-sessions');

    return () => {
      mounted = false;
      removeListener('serato/get-history-sessions');

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <div className="serato-history-ctn">
        <IonRow>
          <IonCol size="12" sizeMd="6">
            {historySessions && historySessions.length > 0 && (
              <BuildItemSelect
                items={historySessions}
                onSelectedItemsChange={handleSelectedHistoryChange}
                selectionType="checkbox"
              />
            )}
            {isLoading && (
              <div style={{ padding: '10px', textAlign: 'center' }}>
                Loading tracks...
              </div>
            )}
          </IonCol>
          <IonCol size="12" sizeMd="6">
            <div
              className="ag-theme-alpine"
              style={{ height: 400, width: '100%' }}
            >
              <AgGridReact
                className={`ag-theme-alpine${theme === 'dark' ? '-dark' : ''}`}
                rowData={tracks}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                loading={isLoading}
              />
            </div>
          </IonCol>
        </IonRow>
      </div>
    </>
  );
};

export default ImportHistory;
